import { useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/**
 * Whether an archiving landed. One `failed` for every way it did not: nothing
 * the person in front of the dialog could do differently tells a refusal from
 * an unreachable server.
 */
export type ArchivalOutcome = "archived" | "failed";

/** Asks somebody to confirm archiving a person, and archives them. */
export const ArchivePersonDialog = ({
  open,
  onClose,
  onArchive,
}: {
  open: boolean;
  onClose: () => void;
  onArchive: () => Promise<ArchivalOutcome>;
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"submitting" | "failed" | undefined>(
    undefined,
  );
  const submitting = status === "submitting";

  const close = () => {
    setStatus(undefined);
    onClose();
  };

  const submit = async () => {
    setStatus("submitting");
    const outcome = await onArchive().catch((): ArchivalOutcome => "failed");

    if (outcome === "archived") {
      close();
      return;
    }

    setStatus("failed");
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("personPage.archive.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("personPage.archive.description")}
          </DialogContentText>
          {status === "failed" ? (
            <Alert severity="error">{t("personPage.archive.failed")}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: submitting,
        }}
        proceed={{
          label: t("personPage.archive.confirm"),
          onClick: submit,
          disabled: submitting,
          kind: "destructive",
        }}
      />
    </Dialog>
  );
};
